import React, { FC, useReducer, useRef } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useTranslation } from 'react-i18next';
import { Formik, FormikProps } from 'formik';
import { Grid, Typography } from '@material-ui/core';
import moment from 'moment';

import {
  ContentInput,
  Network as NetworkType,
  PostType,
  ShareContentDestination,
  ShareContentInput,
  ShareType,
  useExternalShareMutation,
  useShareContentMutation,
} from 'api';
import { useClient, useNotify, useSocialConnections } from 'hooks';
import { FormikNotifier } from 'components';
import { ComposeLayout, ComposeTabBody } from 'features/compose';
import {
  ExternalShareOption,
  ImageUpload,
  initialPostUIState,
  postContextReducer,
  PostUIContext,
  UrlField,
  UrlPreview,
} from 'features/postingV2';
import { isPhotoOnly } from 'features/content/helpers';
import { FACEBOOK_LIMIT, TWITTER_LIMIT, LINKEDIN_LIMIT } from 'constants/index';
import * as paths from 'paths';

import { NETWORKS } from '../constants';
import { INetwork, IShare, IShareNetworkAuth } from '../types';
import { FormButtons, Network, Tabs } from '../components/ExternalShare';

/**
 * An external share creates a post and then a share. The process is transparent
 * to the user. As a result, we pull from multiple sections of the codebase and
 * use all sorts of poor React practices to make this work. This code is a prime
 * example of an API smell, and please don't use these patterns elsewhere
 */
const ExternalShare: FC = () => {
  const notify = useNotify();
  const connections = useSocialConnections();
  const client = useClient();
  const location = useLocation();
  const history = useHistory();

  const { t } = useTranslation();
  const [state, dispatch] = useReducer(postContextReducer, initialPostUIState);
  const [, forceUpdate] = useReducer(x => x + 1, 0);

  const postingFormikRef = useRef<FormikProps<ContentInput>>({} as FormikProps<ContentInput>);
  const sharingFormikRef = useRef<FormikProps<IShare>>({} as FormikProps<IShare>);

  const [shareContent, shareContentResult] = useShareContentMutation({
    onError: notify.mutationError,
    onCompleted: handleCompleted,
  });

  const [processContent, processContentResult] = useExternalShareMutation({
    onError: notify.mutationError,
    onCompleted: mutationResult => {
      const { content_id } = mutationResult.processContent;
      shareToTwitterAndLinkedin(content_id);
      shareToFacebook(content_id);
    },
  });

  const isLoading = shareContentResult.loading || processContentResult.loading;
  const initialContentValues = getInitialContentValues();
  const initialShareValues = getInitialShareValues();

  function getInitialContentValues(): ContentInput {
    return {
      group_ids: ['0'],
      content_source_id: 'manual',
      content_object: {
        post_type: PostType.Quickshare,
        url: '',
        body: '',
        photos: null,
        media: null,
        url_metadata_override: {
          title: '',
          description: '',
        },
      },
    };
  }

  function getInitialShareValues(): IShare {
    return {
      content_id: '',
      share_type: ShareType.Now,
      share_at: null,
      twitter: getNetworkInitialValues(NetworkType.Twitter),
      linkedin: getNetworkInitialValues(NetworkType.Linkedin),
      facebook: getFacebookInitialValues(),
    };
  }

  function getAccountId(provider: NetworkType) {
    const defaultAccount = connections.find(conn => conn.provider === provider);
    return defaultAccount?.id;
  }

  function getNetworkInitialValues(network: NetworkType): IShareNetworkAuth {
    const leadingAccountId = getAccountId(network);
    const limit = network === NetworkType.Twitter ? TWITTER_LIMIT : LINKEDIN_LIMIT;

    return {
      active: true,
      limit,
      network_id: leadingAccountId,
      user_commentary: '',
      share_session_id: '',
    };
  }

  function getFacebookInitialValues() {
    return {
      active: true,
      limit: FACEBOOK_LIMIT,
      user_commentary: '',
      share_session_id: '',
    };
  }

  function handleCompleted() {
    const { share_type, share_at } = sharingFormikRef.current.values;
    let i18nKey = 'sharing:shareSuccess';
    const date = moment.unix(share_at).format('dddd, MMMM Do, YYYY [at] h:mma');

    switch (share_type) {
      case ShareType.Scheduled:
        i18nKey = 'sharing:scheduledSuccess';
        break;
      case ShareType.Now:
        i18nKey = 'sharing:shareSuccess';
        break;
      default:
        throw new Error('Missing ShareType implementation');
    }

    notify.info({ i18n: { i18nKey, values: { date } } });
    location.key ? history.goBack() : history.push(paths.index);
  }

  function handleFacebookDisable() {
    sharingFormikRef.current.setFieldValue('facebook.active', false);
  }

  function shareToTwitterAndLinkedin(contentId: string) {
    const { share_at, share_type, facebook, twitter, linkedin } = sharingFormikRef.current.values;
    const items: ShareContentInput[] = [];
    const common = { share_type, share_at };

    if (twitter.active) {
      const { user_commentary, network_id } = twitter;
      items.push({ user_commentary, network_id, ...common });
    }

    if (linkedin.active) {
      const { user_commentary, network_id } = linkedin;
      items.push({ user_commentary, network_id, ...common });
    }

    if (facebook.active) {
      items.push({
        share_type: ShareType.Frontend,
        destination: ShareContentDestination.Facebook,
      });
    }

    shareContent({
      variables: {
        content_id: contentId,
        items,
      },
    });
  }

  // TODO: We don't have tracking for Facebook posts
  function shareToFacebook(contentId: string) {
    const { facebook } = sharingFormikRef.current.values;

    if (!facebook.active) {
      return;
    }

    const { url } = postingFormikRef.current.values.content_object;
    const href =
      state.shareOption === ExternalShareOption.LinkShare
        ? url
        : `https://${client.subdomain}/content/${contentId}`;

    window?.FB?.ui({
      href,
      method: 'share',
      quote: facebook.user_commentary,
    });
  }

  function handleSubmit() {
    processContent({
      variables: { content: postingFormikRef.current.values },
    });
  }

  function renderNetwork(network: INetwork) {
    const { values } = postingFormikRef.current;
    const isPhotoOnlyContent = values && isPhotoOnly(values);
    const isFacebook = network.provider === NetworkType.Facebook;

    if (isFacebook && isPhotoOnlyContent) {
      return null;
    }

    return (
      <Grid key={network.provider} item>
        <Network network={network} />
      </Grid>
    );
  }

  function renderLink() {
    return (
      <ComposeTabBody>
        <UrlField />
        {state.validUrl && <UrlPreview />}
      </ComposeTabBody>
    );
  }

  function renderImage() {
    return (
      <ComposeTabBody>
        <ImageUpload onImageUpload={handleFacebookDisable} />
      </ComposeTabBody>
    );
  }

  return (
    <PostUIContext.Provider value={{ state, dispatch }}>
      <ComposeLayout
        header={
          <Grid item>
            <Typography variant="h4">{t('sharing:shareToYourNetworks')}</Typography>
          </Grid>
        }
        tabs={<Tabs />}
        body={
          <Formik
            validateOnChange={false}
            onSubmit={() => undefined}
            initialValues={initialContentValues}
            innerRef={ref => (postingFormikRef.current = ref)}>
            <FormikNotifier onChange={forceUpdate}>
              {state.shareOption === ExternalShareOption.LinkShare && renderLink()}
              {state.shareOption === ExternalShareOption.ImageShare && renderImage()}
            </FormikNotifier>
          </Formik>
        }>
        <Formik
          innerRef={ref => (sharingFormikRef.current = ref)}
          validateOnChange={false}
          onSubmit={() => undefined}
          initialValues={initialShareValues}>
          <FormikNotifier onChange={forceUpdate}>
            {NETWORKS.map(renderNetwork)}
            {/* TODO: Waiting for backend support <Grid item><Scheduler /></Grid> */}
          </FormikNotifier>
        </Formik>
        <Grid item>
          <FormButtons
            loading={isLoading}
            onSubmit={handleSubmit}
            postingFormikBag={postingFormikRef.current}
            sharingFormikBag={sharingFormikRef.current}
          />
        </Grid>
      </ComposeLayout>
    </PostUIContext.Provider>
  );
};

export default ExternalShare;
